import { useActiveAds, useAdImpression, useAdClick } from "@/hooks/useAds";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, Megaphone, Sparkles } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface BannerAdProps {
  className?: string;
  onVisibilityChange?: (isVisible: boolean) => void;
}

export const BannerAd = ({ className = "", onVisibilityChange }: BannerAdProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const { data: ads, isLoading } = useActiveAds('banner');
  const handleAdClick = useAdClick();
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  // إبلاغ المكون الأب بتغيير الحالة
  useEffect(() => {
    onVisibilityChange?.(isVisible);
  }, [isVisible, onVisibilityChange]);

  // إجمالي العناصر = الإعلانات + المساحة للإيجار
  const totalItems = (ads?.length || 0) + 1;
  const isShowingPlaceholder = currentAdIndex >= (ads?.length || 0);
  const currentAd = !isShowingPlaceholder ? ads?.[currentAdIndex] : null;
  
  // Track impression when ad is displayed
  useAdImpression(currentAd?.id);

  // Rotate ads every 10 seconds (including placeholder)
  useEffect(() => {
    if (!isLoading) {
      const interval = setInterval(() => {
        setCurrentAdIndex((prev) => (prev + 1) % totalItems);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [totalItems, isLoading]);

  if (!isVisible) return null;

  if (isLoading) return null;

  const handlePlaceholderClick = () => {
    navigate('/advertise');
  };

  if (isShowingPlaceholder) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`fixed top-0 left-0 right-0 z-[60] w-full bg-gradient-to-r from-primary via-emerald-600 to-primary border-b border-primary/30 shadow-lg ${className}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="container mx-auto px-4">
          <div 
            className="flex items-center justify-between py-3 gap-4 cursor-pointer group"
            onClick={handlePlaceholderClick}
          >
            {/* Icon */}
            <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <Megaphone className="w-6 h-6 md:w-7 md:h-7 text-white" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/80 bg-white/20 px-2 py-0.5 rounded-full">
                  <Sparkles className="w-3 h-3" />
                  {isRTL ? 'لمدة محدودة' : 'Limited Time'}
                </span>
              </div>
              <h3 className="font-bold text-white text-lg md:text-xl group-hover:text-white/90 transition-colors">
                {isRTL ? 'مساحة إعلانية متاحة للإيجار' : 'Advertising Space Available'}
              </h3>
              <p className="text-sm text-white/80 hidden md:block">
                {isRTL ? 'اعرض إعلانك أمام آلاف الزوار يومياً' : 'Display your ad to thousands of visitors daily'}
              </p>
            </div>

            {/* Price & CTA */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="text-right hidden sm:block">
                <div className="text-2xl md:text-3xl font-bold text-white">500</div>
                <div className="text-xs text-white/80">{isRTL ? 'ريال / شهر' : 'SAR / month'}</div>
              </div>
              
              <Button 
                variant="secondary" 
                size="sm"
                className="bg-white text-primary hover:bg-white/90 font-bold shadow-lg"
              >
                {isRTL ? 'احجز الآن' : 'Book Now'}
                <ExternalLink className="w-4 h-4 mr-1 rtl:ml-1 rtl:mr-0" />
              </Button>
              
              {/* Close Button */}
              <Button
                variant="ghost"
                size="icon"
                className="w-8 h-8 text-white/70 hover:text-white hover:bg-white/20"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsVisible(false);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  const title = isRTL && currentAd?.title_ar ? currentAd.title_ar : currentAd?.title;
  const description = isRTL && currentAd?.description_ar ? currentAd.description_ar : currentAd?.description;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentAd?.id}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className={`fixed top-0 left-0 right-0 z-[60] w-full bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10 border-b border-primary/20 backdrop-blur-sm ${className}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="container mx-auto px-4">
          <div 
            className="flex items-center justify-between py-3 gap-4 cursor-pointer group"
            onClick={() => handleAdClick(currentAd!.id, currentAd!.link_url)}
          >
            {/* Ad Image */}
            {currentAd?.image_url && (
              <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden shadow-md">
                <img 
                  src={currentAd.image_url} 
                  alt={title || 'Advertisement'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            )}

            {/* Ad Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  {isRTL ? 'إعلان' : 'Ad'}
                </span>
                {title && (
                  <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                    {title}
                  </h3>
                )}
              </div>
              {description && (
                <p className="text-sm text-muted-foreground line-clamp-1">
                  {description}
                </p>
              )}
            </div>

            {/* CTA */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button 
                variant="outline" 
                size="sm"
                className="hidden md:flex items-center gap-1 group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
              >
                {isRTL ? 'اعرف المزيد' : 'Learn More'}
                <ExternalLink className="w-3 h-3" />
              </Button>
              
              {/* Close Button */}
              <Button
                variant="ghost"
                size="icon"
                className="w-6 h-6 opacity-50 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsVisible(false);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Ad Indicators - تشمل المساحة للإيجار */}
          {totalItems > 1 && (
            <div className="flex justify-center gap-1 pb-2">
              {Array.from({ length: totalItems }).map((_, index) => (
                <button
                  key={index}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    index === currentAdIndex ? 'bg-primary' : 'bg-primary/30'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentAdIndex(index);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
